import { useMemo, useState } from "react";
import {
  AllHeader,
  AllPlansFilter,
  AlignmentsSection,
  NoPlans,
} from "../components";
import Sorter from "../components/AllPlans/Sorter.component";
import StickyBar from "../components/AllPlans/StickyBar.component";
import { IPlan } from "../interfaces/api";
import ScrollToTopOnRouteChange from "../shared/ScrollToTop.function";
import { useUpdateBreadcrumbs } from "../shared/updateBreadcrumbs.hook";
import { useZipRootLoaderData } from "../hooks/useZipRootLoaderData";

export const SORT = {
  RECOMMENDED: {
    label: "Recommended",
    sort(plans: IPlan[]) {
      return plans;
    },
  },
  MONTHLY_PREMIUM_ASC: {
    label: "Monthly Premium: Low to High",
    sort(plans: IPlan[]) {
      return plans.sort((a: IPlan, b: IPlan) =>
        a.MonthlyPremiumAmount >= b.MonthlyPremiumAmount ? 1 : -1
      );
    },
  },
  MONTHLY_PREMIUM_DESC: {
    label: "Monthly Premium: High to Low",
    sort(plans: IPlan[]) {
      return plans.sort((a: IPlan, b: IPlan) =>
        a.MonthlyPremiumAmount >= b.MonthlyPremiumAmount ? -1 : 1
      );
    },
  },
  MAX_OUT_OF_POCKET_ASC: {
    label: "Max Out-of-Pocket: Low to High",
    sort(plans: IPlan[]) {
      return plans.sort(
        (a: IPlan, b: IPlan) =>
          a.OutOfPocketMaxInNetworkAmount - b.OutOfPocketMaxInNetworkAmount
      );
    },
  },
  MAX_OUT_OF_POCKET_DESC: {
    label: "Max Out-of-Pocket: High to Low",
    sort(plans: IPlan[]) {
      return plans.sort(
        (a: IPlan, b: IPlan) =>
          b.OutOfPocketMaxInNetworkAmount - a.OutOfPocketMaxInNetworkAmount
      );
    },
  },
  PCP_COPAY_ASC: {
    label: "PCP Copay: Low to High",
    sort(plans: IPlan[]) {
      return plans.sort(
        (a: IPlan, b: IPlan) =>
          a.PCPCopayInNetworkAmount - b.PCPCopayInNetworkAmount
      );
    },
  },
  PCP_COPAY_DESC: {
    label: "PCP Copay: High to Low",
    sort(plans: IPlan[]) {
      return plans.sort(
        (a: IPlan, b: IPlan) =>
          b.PCPCopayInNetworkAmount - a.PCPCopayInNetworkAmount
      );
    },
  },
  SPECIALIST_COPAY_ASC: {
    label: "Specialist Copay: Low to High",
    sort(plans: IPlan[]) {
      return plans.sort(
        (a: IPlan, b: IPlan) =>
          a.SpecialistCopayInNetworkAmount - b.SpecialistCopayInNetworkAmount
      );
    },
  },
  SPECIALIST_COPAY_DESC: {
    label: "Specialist Copay: High to Low",
    sort(plans: IPlan[]) {
      return plans.sort(
        (a: IPlan, b: IPlan) =>
          b.SpecialistCopayInNetworkAmount - a.SpecialistCopayInNetworkAmount
      );
    },
  },
};

const AllPlans = () => {
  useUpdateBreadcrumbs("See All Plans");
  let initialSortBy = sessionStorage.getItem("sort");
  if (initialSortBy === "Lower Maximum Out-of-Pocket Cost") {
    initialSortBy = SORT.MAX_OUT_OF_POCKET_ASC.label;
  } else if (initialSortBy === "Higher FLEX Allowance") {
    initialSortBy = SORT.PCP_COPAY_DESC.label;
  } else if (initialSortBy === "High Over-the-Counter Allowance") {
    initialSortBy = SORT.MONTHLY_PREMIUM_DESC.label;
  } else {
    initialSortBy = SORT.RECOMMENDED.label;
  }
  const { plans } = useZipRootLoaderData();

  const [showFilters, setShowFilters] = useState(false);
  const [sortBy, setSortBy] = useState<string>(initialSortBy);
  const [filters, setFilters] = useState<string[]>([]);

  const filteredAndSortedPlans = useMemo(() => {
    let p = plans.Plans.filter((plan) => {
      return filters.length
        ? (filters.includes("HMO") && plan.HealthPlanlob === "HMO") ||
            (filters.includes("PPO") && plan.HealthPlanlob === "PPO") ||
            (filters.includes("D-SNP") && plan.isDSNP) ||
            (filters.includes("C-SNP") && plan.isCSNP) ||
            (filters.includes("Veterans") && plan.isVeteran) ||
            (filters.includes("FLEX Allowance") && plan.flexAllowance) ||
            (filters.includes("Transportation") && plan.transportation) ||
            (filters.includes("Part B Rebate") && plan.partBRebate) ||
            (filters.includes("Groceries") && plan.groceries) ||
            (filters.includes("Caregiver Support") && plan.caregiver)
        : true;
    });

    const sort = Object.values(SORT).find((s) => s.label === sortBy);
    if (sort) {
      p = sort.sort(p);
    } else {
      console.log(`sort ${sortBy} not found`);
    }

    return p;
  }, [plans, filters, sortBy]);

  const toggleFilters = () => {
    setShowFilters((prevShowFilters) => !prevShowFilters);
  };

  const scrollTo = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };

  if (plans.Plans.length < 1) {
    return <NoPlans />;
  }

  return (
    <>
      <ScrollToTopOnRouteChange />
      <AllHeader plans={plans} />
      <div className="show-for-small-only grid-y" style={{ padding: "0 20px" }}>
        {showFilters ? (
          <button
            type="button"
            className="button hollow"
            onClick={toggleFilters}
          >
            Show Filters
          </button>
        ) : (
          <div className="grid-y">
            <button
              type="button"
              className="button hollow"
              style={{
                color: "#003A5D",
                borderColor: "#003A5D",
              }}
              onClick={toggleFilters}
            >
              Hide Filters
            </button>

            <AllPlansFilter
              filtersArray={filters}
              setActuallFilters={setFilters}
            />
          </div>
        )}
      </div>
      <div className="hide-for-small-only">
        <AllPlansFilter filtersArray={filters} setActuallFilters={setFilters} />
      </div>
      <Sorter
        plans={filteredAndSortedPlans}
        initialSortBy={initialSortBy}
        setSortBy={setSortBy}
      />
      <AlignmentsSection plans={filteredAndSortedPlans} />
      <StickyBar />
      <div
        className="grid-container"
        style={{
          display: "flex",
          justifyContent: "flex-end",
          paddingBottom: 150,
        }}
      >
        <button
          type="button"
          onClick={scrollTo}
          className="primary-color"
          style={{
            cursor: "pointer",
            fontSize: 18,
            fontWeight: 500,
            paddingBottom: 24,
            paddingTop: 24,
          }}
        >
          ^ Back to top
        </button>
      </div>
    </>
  );
};

export default AllPlans;
